import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from 'lucide-react';
import styles from './GameTutorialCarousel.module.css';

const GameTutorialCarousel = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded by default
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Tutorial steps with images and descriptions
  // These are steps 4-7 of the overall tutorial (steps 1-3 are the dashboard tour)
  const tutorialSteps = [
    {
      image: '/tutorial/step1-game-intro.png',
      title: 'Game Instructions',
      description: 'Read the game instructions and tap Play when you\'re ready to start.',
      showNext: true
    },
    {
      image: '/tutorial/step2-positioning.png',
      title: 'Position Yourself',
      description: 'Make sure you\'re positioned so the camera can see your chest and above. Watch the timer and rep counter.',
      showNext: true
    },
    {
      image: '/tutorial/step3-controls.png',
      title: 'Game Controls',
      description: 'You can pause or end the game anytime using the buttons in the top-left corner.',
      showNext: true
    },
    {
      image: '/tutorial/step4-completion.png',
      title: 'Complete & Review',
      description: 'After finishing, review your results and choose to resume or return to the dashboard.',
      showNext: true,
      isLastStep: true
    }
  ];

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % tutorialSteps.length);
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => (prev - 1 + tutorialSteps.length) % tutorialSteps.length);
  };

  const handleDotClick = (index) => {
    setCurrentStep(index);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Mark tutorial as seen and trigger steps 8-9 when carousel closes
  useEffect(() => {
    return () => {
      const patientId = localStorage.getItem('patientId');
      if (patientId) {
        localStorage.setItem(`game_tutorial_seen_${patientId}`, 'true');
        
        // Trigger steps 8-9 (dashboard options tour) after carousel closes
        console.log('📚 Game carousel completed (Steps 4-7)');
        console.log('📚 Starting dashboard options tour (Steps 8-9)');
        
        // Import and start the next tour phase
        import('../../tours/dashboardOptionsTour').then(({ startDashboardOptionsTour }) => {
          setTimeout(() => {
            startDashboardOptionsTour(() => {
              console.log('🎉 All 9 tutorial steps completed!');
            });
          }, 500);
        });
      }
    };
  }, []);

  const currentStepData = tutorialSteps[currentStep];

  return (
    <>
      <div 
        className={`${styles.carouselContainer} ${isExpanded ? styles.expanded : styles.compact}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title} style={{ color: 'white' }}>
            {isExpanded ? currentStepData.title : 'How to Play'}
          </h3>
          <div className={styles.headerButtons}>
            <button
              className={styles.iconButton}
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Minimize' : 'Expand'}
            >
              {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            {onClose && (
              <button
                className={styles.iconButton}
                onClick={onClose}
                aria-label="Close tutorial"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Image */}
        <div className={styles.imageContainer}>
          <img
            src={currentStepData.image}
            alt={currentStepData.title}
            className={styles.image}
            onError={(e) => {
              // Fallback if image doesn't load
              e.target.src = '/dashboard/default-tutorial.png';
            }}
          />
          
          {/* Step indicator */}
          <div className={styles.stepIndicator}>
            Step {currentStep + 4} of 9
          </div>
        </div>

        {/* Description (shown when expanded) */}
        {isExpanded && (
          <div className={styles.description}>
            <p>{currentStepData.description}</p>
          </div>
        )}

        {/* Navigation */}
        <div className={styles.navigation}>
          {currentStep < tutorialSteps.length - 1 ? (
            // Normal navigation for steps 1-3
            <>
              <button
                className={styles.navButton}
                onClick={handlePrevious}
                disabled={currentStep === 0}
                aria-label="Previous step"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Dots */}
              <div className={styles.dots}>
                {tutorialSteps.map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.dot} ${index === currentStep ? styles.activeDot : ''}`}
                    onClick={() => handleDotClick(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>

              <button
                className={styles.navButton}
                onClick={handleNext}
                aria-label="Next step"
              >
                <ChevronRight size={24} />
              </button>
            </>
          ) : (
            // Last step - show Next button to continue to steps 8-9
            <>
              <button
                className={styles.navButton}
                onClick={handlePrevious}
                aria-label="Previous step"
              >
                <ChevronLeft size={24} />
              </button>

              <button
                className={`${styles.navButton} ${styles.nextButton}`}
                onClick={onClose}
                aria-label="Continue to next steps"
              >
                Next →
              </button>
            </>
          )}
        </div>

        {/* Auto-advance hint (compact mode) */}
        {!isExpanded && (
          <div className={styles.hint}>
            Swipe or tap arrows
          </div>
        )}
      </div>

      {/* Backdrop when expanded */}
      {isExpanded && (
        <div 
          className={styles.backdrop}
          onClick={toggleExpanded}
        />
      )}
    </>
  );
};

export default GameTutorialCarousel;
